import { ColumnFiltersState } from '@tanstack/react-table';
import { DataGridColumn, FilterOperator } from './data-grid-types';

export function defaultOperatorFor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  col: DataGridColumn<any> | undefined
): FilterOperator {
  if (!col) return 'ilike';
  if (col.serverOperator) return col.serverOperator;
  if (col.filterVariant === 'number' || col.filterVariant === 'date')
    return 'eq';
  return 'ilike';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function apiFieldFor(col: DataGridColumn<any>): string {
  const ui =
    (col.id as string) ?? (col.accessorKey as string) ?? (col.field as string);
  return (
    (col.filterKey as string) ||
    (col.field as string) ||
    (col.accessorKey as string) ||
    ui
  );
}

function toStartOfDayUTC(d: string) {
  return `${d}T00:00:00Z`;
}
function toEndOfDayUTC(d: string) {
  return `${d}T23:59:59Z`;
}

export function buildQFromColumnFilters(
  filters: ColumnFiltersState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: DataGridColumn<any>[]
): string[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const byId = new Map<string, DataGridColumn<any>>();
  columns.forEach(c => {
    const ui =
      (c.id as string) ?? (c.accessorKey as string) ?? (c.field as string);
    if (ui) byId.set(ui, c);
  });

  const parts: string[] = [];

  for (const { id, value } of filters) {
    const rawTyped = String(value ?? '').trim();
    if (!rawTyped) continue;

    const col = byId.get(id);
    if (!col) continue;

    const field = apiFieldFor(col);
    const op = defaultOperatorFor(col);

    const normalized = col.normalizeFilterValue
      ? col.normalizeFilterValue(rawTyped)
      : rawTyped;

    if (!normalized) continue;

    if (col.filterVariant === 'date') {
      const iso =
        op === 'lte' || op === 'lt'
          ? toEndOfDayUTC(normalized)
          : toStartOfDayUTC(normalized);
      parts.push(`${field}:${op}:${iso}`);
      continue;
    }

    const wireVal = op === 'ilike' ? `%${normalized}%` : normalized;
    parts.push(`${field}:${op}:${wireVal}`);
  }

  return parts;
}

export function parseQToColumnFilters(
  qParams: string | string[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: DataGridColumn<any>[]
): ColumnFiltersState {
  if (!qParams) return [];

  const qArray = Array.isArray(qParams) ? qParams : [qParams];

  const apiToUi = new Map<string, string>();
  columns.forEach(c => {
    const ui =
      (c.id as string) ?? (c.accessorKey as string) ?? (c.field as string);
    const api = apiFieldFor(c);
    if (ui && api) apiToUi.set(api, ui);
  });

  const res: ColumnFiltersState = [];

  for (const chunk of qArray) {
    if (!chunk) continue;

    const m = chunk.match(/^([^:]+):([^:]+):([\s\S]*)$/);
    if (!m) continue;

    const [, field, _op, rawVal] = m;
    const uiId = apiToUi.get(field);
    if (!uiId) continue;

    let val = rawVal;
    if (val.length >= 2 && val.startsWith('%') && val.endsWith('%')) {
      val = val.slice(1, -1);
    }

    let decoded = '';
    try {
      decoded = decodeURIComponent(val);
    } catch {
      decoded = val;
    }

    res.push({ id: uiId, value: decoded });
  }

  return res;
}
